import React, { useState, useEffect } from 'react';
import { formatDistanceToNow, format, parseISO } from 'date-fns';
import Modal from '../components/Modal';
import './Trash.css';

// Format 24-hour time to 12-hour format
const formatTime12Hour = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${hour12}:${minutes} ${ampm}`;
};

const Trash = () => {
  const [activeTab, setActiveTab] = useState('invoices');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ invoices: 0, customers: 0, appointments: 0, services: 0, calendar_events: 0 });
  const [showEmptyModal, setShowEmptyModal] = useState(false);

  const tabs = [
    { id: 'invoices', label: 'Invoices' },
    { id: 'customers', label: 'Customers' },
    { id: 'appointments', label: 'Appointments' },
    { id: 'services', label: 'Services' },
    { id: 'calendar-events', label: 'Events' }
  ];

  useEffect(() => {
    fetchTrashItems();
    fetchStats();
  }, [activeTab]);

  const fetchStats = async () => {
    try {
      const response = await fetch('/api/trash/stats');
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching trash stats:', error);
    }
  };

  const fetchTrashItems = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/trash/${activeTab}`);
      const data = await response.json();
      setItems(data);
    } catch (error) {
      console.error('Error fetching trash items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (id) => {
    try {
      const response = await fetch(`/api/trash/restore/${activeTab}/${id}`, {
        method: 'POST'
      });

      if (response.ok) {
        fetchTrashItems();
        fetchStats();
      } else {
        const error = await response.json();
        alert(`Failed to restore: ${error.error}`);
      }
    } catch (error) {
      console.error('Error restoring item:', error);
      alert('Failed to restore item');
    }
  };

  const handlePermanentDelete = async (id) => {
    if (!window.confirm('Are you sure you want to permanently delete this item? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`/api/trash/${activeTab}/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        fetchTrashItems();
        fetchStats();
      } else {
        const error = await response.json();
        alert(`Failed to delete: ${error.error}`);
      }
    } catch (error) {
      console.error('Error permanently deleting item:', error);
      alert('Failed to permanently delete item');
    }
  };

  const handleEmptyTrash = async () => {
    try {
      const response = await fetch(`/api/trash/empty/${activeTab}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setShowEmptyModal(false);
        fetchTrashItems();
        fetchStats();
      } else {
        const error = await response.json();
        alert(`Failed to empty trash: ${error.error}`);
      }
    } catch (error) {
      console.error('Error emptying trash:', error);
      alert('Failed to empty trash');
    }
  };

  const formatDeletedTime = (deletedAt) => {
    try {
      return formatDistanceToNow(new Date(deletedAt), { addSuffix: true });
    } catch {
      return 'Unknown';
    }
  };

  const renderItemContent = (item) => {
    switch (activeTab) {
      case 'invoices':
        return (
          <>
            <div className="trash-item__main">
              <span className="trash-item__name">{item.invoice_number}</span>
              <span className="trash-item__detail">
                {item.first_name} {item.last_name}
                {item.customer_deleted_at && <span className="trash-item__badge">Customer trashed</span>}
              </span>
            </div>
            <div className="trash-item__meta">
              <span className="trash-item__amount">${parseFloat(item.total).toFixed(2)}</span>
              <span className="trash-item__status">{item.status}</span>
            </div>
          </>
        );

      case 'customers':
        return (
          <>
            <div className="trash-item__main">
              <span className="trash-item__name">{item.first_name} {item.last_name}</span>
              <span className="trash-item__detail">{item.phone}</span>
            </div>
            <div className="trash-item__meta">
              {item.trashed_invoices > 0 && (
                <span className="trash-item__related">{item.trashed_invoices} invoice{item.trashed_invoices > 1 ? 's' : ''}</span>
              )}
              {item.trashed_appointments > 0 && (
                <span className="trash-item__related">{item.trashed_appointments} appointment{item.trashed_appointments > 1 ? 's' : ''}</span>
              )}
            </div>
          </>
        );

      case 'appointments':
        return (
          <>
            <div className="trash-item__main">
              <span className="trash-item__name">
                {format(parseISO(item.scheduled_date), 'MMM d, yyyy')} at {formatTime12Hour(item.scheduled_time)}
              </span>
              <span className="trash-item__detail">
                {item.first_name} {item.last_name}
                {item.customer_deleted_at && <span className="trash-item__badge">Customer trashed</span>}
              </span>
            </div>
            <div className="trash-item__meta">
              <span className="trash-item__status">{item.status}</span>
            </div>
          </>
        );

      case 'services':
        return (
          <>
            <div className="trash-item__main">
              <span className="trash-item__name">{item.name}</span>
              <span className="trash-item__detail">{item.category}</span>
            </div>
            <div className="trash-item__meta">
              {item.base_price_car && (
                <span className="trash-item__amount">${parseFloat(item.base_price_car).toFixed(2)}</span>
              )}
            </div>
          </>
        );

      case 'calendar-events':
        return (
          <>
            <div className="trash-item__main">
              <span className="trash-item__name">{item.title}</span>
              <span className="trash-item__detail">
                {format(parseISO(item.event_date), 'MMM d, yyyy')}
                {item.event_time && ` at ${formatTime12Hour(item.event_time)}`}
              </span>
            </div>
            <div className="trash-item__meta">
              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', backgroundColor: item.color || '#C9A227' }}></span>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  const getStatCount = (tabId) => {
    const key = tabId.replace('-', '_');
    return stats[key] || 0;
  };

  const totalTrash = stats.invoices + stats.customers + stats.appointments + stats.services + stats.calendar_events;

  return (
    <div className="trash-page">
      <div className="page-header">
        <h1 className="page-header__title">Trash</h1>
        {getStatCount(activeTab) > 0 && (
          <div className="page-header__actions">
            <button
              className="btn btn--danger"
              onClick={() => setShowEmptyModal(true)}
            >
              Empty {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
            </button>
          </div>
        )}
      </div>

      <div className="trash-toolbar">
        <div className="filter-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`filter-tab ${activeTab === tab.id ? 'filter-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
              {getStatCount(tab.id) > 0 && (
                <span className="trash-badge">{getStatCount(tab.id)}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
        </div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </div>
          <h3 className="empty-state__title">No {activeTab} in trash</h3>
          <p>Deleted {activeTab} will appear here</p>
        </div>
      ) : (
        <div className="trash-list">
          {items.map((item) => (
            <div key={item.id} className="trash-item">
              <div className="trash-item__content">
                {renderItemContent(item)}
                <div className="trash-item__deleted">
                  Deleted {formatDeletedTime(item.deleted_at)}
                </div>
              </div>
              <div className="trash-item__actions">
                <button
                  className="btn btn--primary btn--small"
                  onClick={() => handleRestore(item.id)}
                >
                  Restore
                </button>
                <button
                  className="btn btn--danger btn--small"
                  onClick={() => handlePermanentDelete(item.id)}
                >
                  Delete Forever
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={showEmptyModal}
        onClose={() => setShowEmptyModal(false)}
        title="Empty Trash"
      >
        <div className="empty-trash-confirm">
          <p>Are you sure you want to permanently delete all {activeTab} from trash?</p>
          <p className="empty-trash-confirm__warning">This action cannot be undone.</p>
          <div className="modal__footer">
            <button
              className="btn btn--secondary"
              onClick={() => setShowEmptyModal(false)}
            >
              Cancel
            </button>
            <button
              className="btn btn--danger"
              onClick={handleEmptyTrash}
            >
              Delete All {getStatCount(activeTab)} {activeTab}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Trash;
